<script setup lang="ts">
// Tracking devices: the caller's own Traccar-compatible ingest credentials. A
// device's uniqueId is what the phone types in as its identifier, and the token
// minted alongside it is the credential that phone uploads positions with.
//
// The token is a live secret shown exactly once, so the enrolment response
// lives in this component's state and nowhere else — never a store, never
// localStorage. Navigating away is what forgets it.
import { onMounted, reactive, ref } from "vue";

import { apiClient } from "@/client";
import LocalizedTime from "@/components/LocalizedTime.vue";
import ToastNotification from "@/components/ToastNotification.vue";
import ButtonLoading from "@/components/common/ButtonLoading.vue";
import { copyToClipboard } from "@/lib/tileService";
import type {
	TrackingDevice,
	TrackingDeviceEnrolRequest,
	TrackingDeviceEnrolResponse,
} from "@/type/api";

const loading = ref(true);
const error = ref("");
const devices = ref<TrackingDevice[]>([]);

const form = reactive({
	label: "",
	username: "",
	uniqueId: "",
});
const submitting = ref(false);
const formError = ref("");

// The enrolment response, kept only until the operator navigates away or
// dismisses it. It carries the token and is the only place the token exists in
// the browser.
const enrolled = ref<TrackingDeviceEnrolResponse | null>(null);

// Revoking is two steps inline rather than a modal: one click arms the row, the
// second commits. Arming another row disarms the first, so at most one
// confirmation is ever open.
const confirmingId = ref<number | null>(null);
// The row whose revoke request is in flight, so only that row's buttons spin.
const revokingId = ref<number | null>(null);

const toast = reactive({ show: false, title: "", message: "" });

// Which enrolment value was last copied, for the transient "Copied" label.
// Reset on a timer so the button reads as an action rather than a state.
const copied = ref<"" | "serverUrl" | "token">("");
let copiedTimer: ReturnType<typeof setTimeout> | undefined;

onMounted(load);

function showToast(title: string, message: string): void {
	toast.title = title;
	toast.message = message;
	toast.show = true;
}

/** The caller's devices. Does not touch `loading`: a reload after an enrolment
 * or a revoke must not replace the page with a spinner and hide the result the
 * operator is reading. */
async function loadDevices(): Promise<void> {
	try {
		devices.value = await apiClient.JSONGet<TrackingDevice[]>(
			"/api/mobile/trackingDevices",
		);
	} catch (e: any) {
		error.value =
			e?.response?.data?.message ||
			e?.message ||
			"Failed to load tracking devices.";
	}
}

async function load(): Promise<void> {
	loading.value = true;
	error.value = "";
	await loadDevices();
	loading.value = false;
}

async function enrol(): Promise<void> {
	submitting.value = true;
	formError.value = "";
	try {
		// The optional fields are omitted rather than sent blank: a blank
		// username would ask the server to resolve a user named "", and a blank
		// uniqueId must mean "generate one".
		const request: TrackingDeviceEnrolRequest = { label: form.label.trim() };
		const username = form.username.trim();
		if (username !== "") request.username = username;
		const uniqueId = form.uniqueId.trim();
		if (uniqueId !== "") request.uniqueId = uniqueId;

		enrolled.value = await apiClient.JSONPost<TrackingDeviceEnrolResponse>(
			"/api/mobile/trackingDevices",
			request,
		);
		form.label = "";
		form.username = "";
		form.uniqueId = "";
		error.value = "";
		await loadDevices();
	} catch (e: any) {
		const message =
			e?.response?.data?.message ||
			e?.message ||
			"Failed to add the tracking device.";
		formError.value = message;
		showToast("Add tracking device failed", message);
	} finally {
		submitting.value = false;
	}
}

async function revoke(device: TrackingDevice): Promise<void> {
	revokingId.value = device.id;
	try {
		await apiClient.JSONPost<{ revoked: boolean }>(
			`/api/mobile/trackingDevices/${device.id}/revoke`,
		);
		confirmingId.value = null;
		await loadDevices();
	} catch (e: any) {
		showToast(
			"Revoke failed",
			e?.response?.data?.message ||
				e?.message ||
				"Failed to revoke the tracking device.",
		);
	} finally {
		revokingId.value = null;
	}
}

async function copy(field: "serverUrl" | "token"): Promise<void> {
	const value =
		field === "serverUrl" ? enrolled.value?.serverUrl : enrolled.value?.token;
	if (!value) return;
	await copyToClipboard(value);
	copied.value = field;
	if (copiedTimer) clearTimeout(copiedTimer);
	copiedTimer = setTimeout(() => {
		copied.value = "";
	}, 1500);
}
</script>

<template>
	<div class="container py-4">
		<div class="d-flex justify-content-between align-items-center mb-4">
			<div class="d-flex align-items-center">
				<RouterLink
					class="btn btn-outline-secondary me-3"
					to="/_/configuration"
					title="Back to Settings"
				>
					<i class="bi bi-arrow-left"></i>
				</RouterLink>
				<div>
					<h1 class="mb-0">Tracking devices</h1>
					<p class="text-muted mb-0">
						Phones reporting their position through the Traccar app
					</p>
				</div>
			</div>
		</div>

		<!-- Least privilege, said on the page rather than left to be discovered:
		     the URL and token are made to be pasted into a phone, and a pasted
		     secret does get seen. -->
		<p class="text-muted small">
			A device credential can only upload positions for its own device and read
			them back. A leaked URL grants nothing else: it cannot sign in to Nidus,
			see another device, or change anything.
		</p>

		<div v-if="error" class="alert alert-danger" role="alert">
			{{ error }}
		</div>

		<div v-if="loading" class="text-center p-5">
			<div class="spinner-border text-primary" role="status">
				<span class="visually-hidden">Loading…</span>
			</div>
		</div>

		<template v-else>
			<div class="card mb-4 tracking-card">
				<div class="card-header">
					<h2 class="h5 mb-0">Devices</h2>
				</div>
				<div class="card-body">
					<div v-if="devices.length === 0" class="text-muted">
						No tracking devices yet. Enrol one below to get the server URL and
						token to enter in the Traccar app.
					</div>

					<table v-else class="table table-sm align-middle mb-0">
						<thead>
							<tr>
								<th scope="col">Label</th>
								<th scope="col">Device identifier</th>
								<th scope="col">Created</th>
								<th scope="col">Last seen</th>
								<th scope="col">Revoked</th>
								<th scope="col" class="text-end">Actions</th>
							</tr>
						</thead>
						<tbody>
							<tr v-for="device in devices" :key="device.id">
								<td>{{ device.label }}</td>
								<td>
									<code>{{ device.uniqueId }}</code>
								</td>
								<td class="small">
									<LocalizedTime :datetime="device.createdAt" />
								</td>
								<td class="small">
									<LocalizedTime
										v-if="device.lastSeenAt"
										:datetime="device.lastSeenAt"
									/>
									<span v-else class="text-muted">—</span>
								</td>
								<td>
									<template v-if="device.revokedAt">
										<span class="badge text-bg-secondary">revoked</span>
										<div class="small text-muted">
											<LocalizedTime :datetime="device.revokedAt" />
										</div>
									</template>
									<span v-else class="text-muted">—</span>
								</td>
								<td class="text-end">
									<template v-if="device.revokedAt === null">
										<template v-if="confirmingId === device.id">
											<span class="text-muted small me-2">
												Uploads stop immediately.
											</span>
											<button
												class="btn btn-sm btn-danger"
												:disabled="revokingId === device.id"
												@click="revoke(device)"
											>
												<span
													v-if="revokingId === device.id"
													class="spinner-border spinner-border-sm me-1"
													role="status"
													aria-hidden="true"
												></span>
												Confirm revoke
											</button>
											<button
												class="btn btn-sm btn-outline-secondary ms-1"
												:disabled="revokingId === device.id"
												@click="confirmingId = null"
											>
												Cancel
											</button>
										</template>
										<button
											v-else
											class="btn btn-sm btn-outline-danger"
											@click="confirmingId = device.id"
										>
											Revoke
										</button>
									</template>
								</td>
							</tr>
						</tbody>
					</table>
				</div>
			</div>

			<div class="card mb-4 tracking-card">
				<div class="card-header">
					<h2 class="h5 mb-0">Add tracking device</h2>
				</div>
				<div class="card-body">
					<form @submit.prevent="enrol">
						<div class="row g-3">
							<div class="col-md-4">
								<label class="form-label" for="tracking-label">Label</label>
								<input
									id="tracking-label"
									v-model="form.label"
									type="text"
									class="form-control"
									placeholder="Field iPhone"
									required
								/>
								<div class="form-text">
									Name the credential so its rows are recognisable here.
								</div>
							</div>

							<div class="col-md-4">
								<label class="form-label" for="tracking-username">
									Username (optional)
								</label>
								<input
									id="tracking-username"
									v-model="form.username"
									type="text"
									class="form-control"
									autocomplete="off"
									placeholder="Another member's username"
								/>
								<div class="form-text">
									Enrols the device on another member's behalf. Only an account
									owner can do this.
								</div>
							</div>

							<div class="col-md-4">
								<label class="form-label" for="tracking-unique-id">
									Device identifier (optional)
								</label>
								<input
									id="tracking-unique-id"
									v-model="form.uniqueId"
									type="text"
									class="form-control"
									autocomplete="off"
									placeholder="48213377"
								/>
								<div class="form-text">
									Paste the 8-digit identifier the Traccar app generated on
									first launch. Leave blank and the server generates one.
								</div>
							</div>
						</div>
					</form>

					<div
						v-if="formError"
						class="alert alert-danger mt-3 mb-0"
						role="alert"
					>
						{{ formError }}
					</div>

					<div class="mt-3">
						<ButtonLoading
							text="Add tracking device"
							icon="bi bi-plus-lg"
							:loading="submitting"
							:disabled="form.label.trim() === ''"
							@click="enrol"
						/>
					</div>
				</div>
			</div>

			<!-- The one showing of the token. Everything the operator needs to
			     finish setting up the phone is here, and none of it is stored. -->
			<div v-if="enrolled" class="card mb-4 tracking-card border-success">
				<div
					class="card-header d-flex justify-content-between align-items-center"
				>
					<h2 class="h5 mb-0">Device added</h2>
					<button
						class="btn btn-sm btn-outline-secondary"
						@click="enrolled = null"
					>
						Dismiss
					</button>
				</div>
				<div class="card-body">
					<p class="mb-3">
						<strong>{{ enrolled.device.label }}</strong> — identifier
						<code>{{ enrolled.device.uniqueId }}</code>
					</p>

					<div class="alert alert-warning" role="alert">
						This token is shown once — it cannot be retrieved again.
					</div>

					<!-- The primary setup path: one scan sets the URL, the
					     identifier and the tracking policy. The manual fields
					     below stay for a client without a scanner and for the
					     operator who wants to check what the code carries. -->
					<div class="row g-3 mb-3">
						<div v-if="enrolled.qrCode" class="col-12 col-md-auto text-center">
							<img
								:src="enrolled.qrCode"
								alt="QR code carrying the tracking server URL and settings"
								class="tracking-qr border rounded bg-white p-2"
								width="220"
								height="220"
							/>
						</div>
						<div class="col">
							<p class="mb-2">
								<strong>Scan this in the Traccar app</strong> — open the
								settings and choose the QR code option.
							</p>
							<p class="text-muted small mb-2">
								One scan sets the Server URL, the Device identifier and the
								tracking settings: high accuracy, one fix per minute, offline
								buffering on, wake lock off, stop detection on.
							</p>
							<p class="text-muted small mb-0">
								The identifier is
								<code>{{ enrolled.device.uniqueId }}</code
								>. It is what the phone reports, so it must stay paired with
								this token.
							</p>
						</div>
					</div>

					<label class="form-label" for="tracking-server-url">
						Server URL
					</label>
					<div class="input-group mb-3">
						<input
							id="tracking-server-url"
							class="form-control"
							readonly
							:value="enrolled.serverUrl"
						/>
						<button
							class="btn btn-outline-secondary"
							type="button"
							@click="copy('serverUrl')"
						>
							<i class="bi bi-clipboard me-1"></i>
							{{ copied === "serverUrl" ? "Copied" : "Copy" }}
						</button>
					</div>

					<label class="form-label" for="tracking-token"> Token </label>
					<div class="input-group">
						<input
							id="tracking-token"
							class="form-control font-monospace"
							readonly
							:value="enrolled.token"
						/>
						<button
							class="btn btn-outline-secondary"
							type="button"
							@click="copy('token')"
						>
							<i class="bi bi-clipboard me-1"></i>
							{{ copied === "token" ? "Copied" : "Copy" }}
						</button>
					</div>

					<div class="form-text">
						Enter the server URL in the app's Server URL setting and the
						identifier above in Device identifier.
					</div>
				</div>
			</div>
		</template>

		<ToastNotification
			v-model:show="toast.show"
			:title="toast.title"
			:message="toast.message"
		/>
	</div>
</template>

<style scoped>
.tracking-card {
	border-left: 5px solid #0d6efd;
}

/* The code is drawn as sharp module blocks; never smooth it when scaled. */
.tracking-qr {
	image-rendering: pixelated;
	max-width: 100%;
	height: auto;
}
</style>
